import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CONSULT_BG } from "@/lib/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  email: "",
  country: "",
  years_trading: "",
  markets: "",
  biggest_struggle: "",
  why_now: "",
  referral: "",
  website: "", // honeypot — must stay empty
};

const yearsOptions = [
  "Less than 1 year",
  "1 – 3 years",
  "3 – 5 years",
  "5 – 10 years",
  "10+ years",
];

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  viewport: { once: true, margin: "-80px" },
};

export default function ApplicationForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Application received", {
        description: "KP reviews every application personally. Expect to hear back within 5 business days.",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail.map((d) => d.msg).join(", ")
          : null) ||
        "Please review the form and try again.";
      toast.error("Submission failed", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-hairline rounded-none px-0 py-3 text-base text-bone placeholder:text-smoke focus:outline-none focus:border-platinum transition-colors";

  return (
    <section
      id="apply"
      data-testid="application-section"
      className="relative border-t border-hairline"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: `url(${CONSULT_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/95 to-ink" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-28 lg:py-40">
        <motion.div {...fade} className="max-w-2xl">
          <span className="overline" data-testid="application-overline">Application — Free Consultation</span>
          <h2 className="font-serif text-4xl md:text-6xl font-medium leading-[0.95] mt-6 tracking-tight">
            Sit for the chair.
          </h2>
          <p className="font-sans text-sand text-base md:text-lg leading-relaxed mt-8 max-w-xl">
            Applications are reviewed personally by KP. Not every applicant is accepted —
            the work is private, the calendar is finite, and fit matters more than fees.
            There is no obligation, no pitch, and no charge for the consultation.
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            {...fade}
            data-testid="application-success"
            className="mt-20 max-w-2xl border-t border-b border-hairline py-16"
          >
            <span className="overline">Received</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-4">
              Your application is in.
            </h3>
            <p className="font-sans text-sand mt-6 leading-relaxed">
              KP reviews every application personally. If there is fit, you will receive a
              private invitation to the consultation within five business days. We do not
              add you to any list. We do not market to you. The next move is ours.
            </p>
            <button
              type="button"
              data-testid="application-reset-btn"
              onClick={() => setSubmitted(false)}
              className="mt-10 link-underline text-sm tracking-[0.2em] uppercase"
            >
              Submit another application
            </button>
          </motion.div>
        ) : (
          <motion.form
            {...fade}
            onSubmit={handleSubmit}
            data-testid="application-form"
            className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10"
          >
            <Field label="Full name" required>
              <input
                data-testid="input-full-name"
                required
                value={form.full_name}
                onChange={update("full_name")}
                className={inputCls}
                placeholder="As you would sign a contract"
                autoComplete="name"
              />
            </Field>

            <Field label="Email" required>
              <input
                data-testid="input-email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                className={inputCls}
                placeholder="you@domain.com"
                autoComplete="email"
              />
            </Field>

            <Field label="Country / time zone">
              <input
                data-testid="input-country"
                value={form.country}
                onChange={update("country")}
                className={inputCls}
                placeholder="e.g. United Kingdom — GMT"
              />
            </Field>

            <Field label="Years actively trading" required>
              <select
                data-testid="input-years"
                required
                value={form.years_trading}
                onChange={update("years_trading")}
                className={`${inputCls} appearance-none cursor-pointer`}
              >
                <option value="" disabled>Select…</option>
                {yearsOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Field>

            <Field label="Markets you trade" className="md:col-span-2">
              <input
                data-testid="input-markets"
                value={form.markets}
                onChange={update("markets")}
                className={inputCls}
                placeholder="e.g. FX majors, NQ futures, crypto perps"
              />
            </Field>

            <Field
              label="What is your biggest psychological struggle right now?"
              required
              className="md:col-span-2"
            >
              <textarea
                data-testid="input-struggle"
                required
                rows={4}
                value={form.biggest_struggle}
                onChange={update("biggest_struggle")}
                className={`${inputCls} resize-none`}
                placeholder="Be specific. Be honest. This is the only thing that determines fit."
              />
            </Field>

            <Field
              label="Why now? Why mentorship over another course or system?"
              required
              className="md:col-span-2"
            >
              <textarea
                data-testid="input-why-now"
                required
                rows={4}
                value={form.why_now}
                onChange={update("why_now")}
                className={`${inputCls} resize-none`}
                placeholder="The timing of a decision tells us more than the decision itself."
              />
            </Field>

            <Field label="How did you hear about KP Capital?" className="md:col-span-2">
              <input
                data-testid="input-referral"
                value={form.referral}
                onChange={update("referral")}
                className={inputCls}
                placeholder="A referral, a mentee, a piece of writing…"
              />
            </Field>

            {/* Honeypot — hidden from humans, visible to bots. Must stay empty. */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                  name="website"
                />
              </label>
            </div>

            <div className="md:col-span-2 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-hairline">
              <p className="text-xs tracking-[0.18em] uppercase text-smoke max-w-md">
                By applying you accept that submissions are reviewed at KP's discretion and not every applicant will be invited.
              </p>
              <button
                type="submit"
                data-testid="application-submit-btn"
                disabled={submitting}
                className="group inline-flex items-center gap-4 bg-platinum text-ink px-8 py-4 rounded-none text-xs tracking-[0.25em] uppercase font-medium hover:bg-bone transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{submitting ? "Submitting…" : "Submit Application"}</span>
                <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </button>
            </div>
          </motion.form>
        )}
      </div>
    </section>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block overline mb-3">
        {label} {required && <span className="text-platinum">*</span>}
      </span>
      {children}
    </label>
  );
}
